import React, { useState, useEffect, useRef } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Package, Search, X } from 'lucide-react';
import * as Icons from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

export const AllgemeineAbschriften = () => {
  const { user } = useAuth();
  const [types, setTypes] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // Form state
  const [selectedType, setSelectedType] = useState('');
  const [selectedLocation, setSelectedLocation] = useState('');
  const [productName, setProductName] = useState('');
  const [articleNumber, setArticleNumber] = useState('');
  const [ean, setEan] = useState('');
  const [quantity, setQuantity] = useState('');

  // Autocomplete state
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const autocompleteRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  useEffect(() => {
    fetchData();
    
    // Click outside handler für Autocomplete
    const handleClickOutside = (event) => {
      if (autocompleteRef.current && !autocompleteRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchData = async () => {
    try {
      // Fetch types (works for all roles)
      const typesRes = await axios.get(`${API}/api/general-writeoff-types`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      
      setTypes(typesRes.data || []);

      // Fetch locations based on role
      let locationsRes;
      try {
        if (user?.role === 'admin' || user?.role === 'stationsleitung') {
          locationsRes = await axios.get(`${API}/api/admin/locations`, {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
          });
        } else {
          // Employee uses user/locations endpoint
          locationsRes = await axios.get(`${API}/api/user/locations`, {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
          });
        }
        
        setLocations(locationsRes.data || []);

        // Auto-select location for employees
        if (user?.role === 'employee' && locationsRes.data?.length > 0) {
          setSelectedLocation(locationsRes.data[0].id);
        }
      } catch (locationError) {
        console.error('Failed to fetch locations:', locationError);
        // Continue even if locations fail - types are more critical
        setLocations([]);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  };

  const handleProductNameChange = (value) => {
    setProductName(value);

    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (value.length >= 2) {
      // Debounce search
      searchTimeoutRef.current = setTimeout(() => {
        fetchSuggestions(value);
      }, 300);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const fetchSuggestions = async (query) => {
    setLoadingSuggestions(true);
    try {
      const response = await axios.get(`${API}/api/general-writeoffs/autocomplete`, {
        params: { q: query },
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      setSuggestions(response.data || []);
      setShowSuggestions(response.data.length > 0);
    } catch (error) {
      console.error('Failed to fetch suggestions:', error);
      setSuggestions([]);
    } finally {
      setLoadingSuggestions(false);
    }
  };

  const handleSelectSuggestion = (suggestion) => {
    setProductName(suggestion.product_name);
    setArticleNumber(suggestion.article_number || '');
    setEan(suggestion.ean || '');
    setShowSuggestions(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedType || !selectedLocation || !productName.trim() || !quantity) {
      toast.error('Bitte füllen Sie alle Pflichtfelder aus');
      return;
    }

    const quantityNum = parseFloat(quantity);
    if (isNaN(quantityNum) || quantityNum <= 0) {
      toast.error('Bitte geben Sie eine gültige Menge ein');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(
        `${API}/api/general-writeoffs`,
        {
          type_id: selectedType,
          location_id: selectedLocation,
          product_name: productName.trim(),
          article_number: articleNumber.trim() || null,
          ean: ean.trim() || null,
          quantity: quantityNum,
          user_id: 'dummy', // Backend setzt die richtige user_id
          customer_id: 'dummy'
        },
        {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }
      );

      toast.success('Abschrift erfolgreich erfasst');
      
      // Reset form
      setSelectedType('');
      setProductName('');
      setArticleNumber('');
      setEan('');
      setQuantity('');
    } catch (error) {
      console.error('Failed to create writeoff:', error);
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setSubmitting(false);
    }
  };

  const renderIcon = (iconName) => {
    const IconComponent = Icons[iconName];
    return IconComponent ? <IconComponent className="h-5 w-5" /> : <Icons.PackageX className="h-5 w-5" />;
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-muted-foreground">Lädt...</div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-4 sm:space-y-6 pb-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold" style={{fontFamily: 'Manrope, sans-serif'}}>
            Allgemeine Abschriften
          </h1>
          <p className="text-sm sm:text-base text-muted-foreground mt-2">
            Abschriften für alle anderen Produkte dokumentieren
          </p>
        </div>

        {/* Form */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base sm:text-lg">Neue Abschrift erfassen</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Type Selection */}
              <div className="space-y-2">
                <Label htmlFor="type">Abschrift-Typ *</Label>
                {types.length === 0 ? (
                  <div className="p-4 border border-dashed rounded-lg text-center text-sm text-muted-foreground">
                    Keine Typen verfügbar. Bitte kontaktieren Sie Ihren Administrator.
                  </div>
                ) : (
                  <Select value={selectedType} onValueChange={setSelectedType}>
                    <SelectTrigger id="type">
                      <SelectValue placeholder="Typ auswählen" />
                    </SelectTrigger>
                    <SelectContent>
                      {types.map((type) => (
                        <SelectItem key={type.id} value={type.id}>
                          <div className="flex items-center gap-2">
                            <div style={{ color: type.color }}>
                              {renderIcon(type.icon)}
                            </div>
                            <span>{type.name}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>

              {/* Location Selection */}
              {user?.role === 'admin' && (
                <div className="space-y-2">
                  <Label htmlFor="location">Standort *</Label>
                  <Select value={selectedLocation} onValueChange={setSelectedLocation}>
                    <SelectTrigger id="location">
                      <SelectValue placeholder="Standort auswählen" />
                    </SelectTrigger>
                    <SelectContent>
                      {locations.map((location) => (
                        <SelectItem key={location.id} value={location.id}>
                          {location.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Product Name with Autocomplete */}
              <div className="space-y-2" ref={autocompleteRef}>
                <Label htmlFor="productName">Produktname *</Label>
                <div className="relative">
                  <div className="relative">
                    <Input
                      id="productName"
                      placeholder="Produktname eingeben..."
                      value={productName}
                      onChange={(e) => handleProductNameChange(e.target.value)}
                      onFocus={() => {
                        if (suggestions.length > 0) setShowSuggestions(true);
                      }}
                      className="pr-10"
                    />
                    {productName && (
                      <button
                        type="button"
                        onClick={() => {
                          setProductName('');
                          setArticleNumber('');
                          setEan('');
                          setSuggestions([]);
                          setShowSuggestions(false);
                        }}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:text-gray-400"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>

                  {/* Autocomplete Dropdown */}
                  {showSuggestions && suggestions.length > 0 && (
                    <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-900 border rounded-lg shadow-lg max-h-60 overflow-auto">
                      {loadingSuggestions && (
                        <div className="p-3 text-sm text-center text-muted-foreground">
                          <Search className="h-4 w-4 animate-spin mx-auto mb-1" />
                          Suche...
                        </div>
                      )}
                      {!loadingSuggestions && suggestions.map((suggestion, index) => (
                        <button
                          key={index}
                          type="button"
                          onClick={() => handleSelectSuggestion(suggestion)}
                          className="w-full px-4 py-3 text-left hover:bg-gray-50 dark:bg-gray-800 border-b last:border-b-0 transition-colors"
                        >
                          <div className="font-medium text-sm">{suggestion.product_name}</div>
                          <div className="text-xs text-muted-foreground mt-1 space-x-2">
                            {suggestion.article_number && (
                              <span>Art.-Nr.: {suggestion.article_number}</span>
                            )}
                            {suggestion.ean && (
                              <span>EAN: {suggestion.ean}</span>
                            )}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  Tippen Sie mindestens 2 Zeichen für Vorschläge
                </p>
              </div>

              {/* Article Number & EAN */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="articleNumber">Artikelnummer</Label>
                  <Input
                    id="articleNumber"
                    placeholder="Optional"
                    value={articleNumber}
                    onChange={(e) => setArticleNumber(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ean">EAN</Label>
                  <Input
                    id="ean"
                    placeholder="Optional"
                    value={ean}
                    onChange={(e) => setEan(e.target.value)}
                  />
                </div>
              </div>

              {/* Quantity */}
              <div className="space-y-2">
                <Label htmlFor="quantity">Menge *</Label>
                <Input
                  id="quantity"
                  type="number"
                  step="0.01"
                  min="0.01"
                  placeholder="z.B. 5 oder 2.5"
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                />
              </div>

              {/* Submit Button */}
              <Button type="submit" disabled={submitting || types.length === 0} className="w-full sm:w-auto">
                <Plus className="h-4 w-4 mr-2" />
                {submitting ? 'Speichert...' : 'Abschrift erfassen'}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Info Card */}
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="pt-6">
            <div className="flex gap-3">
              <Package className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-900">
                <p className="font-semibold mb-1">💡 Tipp</p>
                <p>
                  Die Autocomplete-Funktion schlägt Produkte aus früheren MHD- und Allgemein-Abschriften vor. 
                  Bei Auswahl werden Artikelnummer und EAN automatisch ausgefüllt.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};
